import React, { useEffect, useRef, useState } from "react";
import { Button, Row, Form, InputGroup, Col, Modal } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import NumberPagination from "../../Pagination";
import SideMenu from "../../Components/SideMenu";
import {
  addUser,
  deleteUserByAdmin,
  editProfileByAdmin,
  getFrontEndBranch,
  getUserRole,
  getUsers,
  resetUsersList,
} from "../../reducers/commonReducer";
import UserAction from "./UserAction";
import moment from "moment";
import { home, quickReply, uploadedFiles, users } from "../../const";
import LastLocation from "../../ComponentsWeb/Sub/LastLocation";
import UpdateCustomer from "./UpdateCustomer";
import { postApi } from "../../helpers/apiStructure";

const escapeCsvValue = (value = "") => {
  const stringValue = String(value ?? "");
  return `"${stringValue.replace(/"/g, '""')}"`;
};

const buildCsvBlob = (rows = []) => {
  const headers = ["Name", "Branch", "Phone", "Status", "Role", "Last Active"];
  const csvRows = [
    headers.map((header) => escapeCsvValue(header)).join(","),
    ...rows.map((row) =>
      [
        row?.name,
        row?.branchName,
        row?.phone,
        row?.status,
        row?.role,
        row?.lastActive,
      ]
        .map((value) => escapeCsvValue(value))
        .join(",")
    ),
  ];

  return new Blob(["\uFEFF" + csvRows.join("\n")], {
    type: "text/csv;charset=utf-8;",
  });
};

const Users = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const width = useRef(window.innerWidth);
  const queryParameters = new URLSearchParams(window.location.search);
  const branchFilter = queryParameters.get("branch");
  const roleFilter = queryParameters.get("role");
  const branchName = queryParameters.get("branchName");
  const statusFilter = queryParameters.get("status");
  const startdate = queryParameters.get("startDate");
  const enddate = queryParameters.get("endDate");
  const pagenumber = queryParameters.get("page");

  const [search, setSearch] = useState();
  const [page, setPage] = useState(pagenumber);
  const [startDate, setStartDate] = useState(startdate);
  const [endDate, setEndDate] = useState(enddate);
  const [status, setStatus] = useState(
    statusFilter && /true/.test(statusFilter) 
  );
  const [branch, setBranch] = useState(branchFilter);
  const [role, setRole] = useState(roleFilter);
  const [exportingCsv, setExportingCsv] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const baseUrl = `${window.location.protocol}//${window.location.host}`;

  useEffect(() => {
    if (localStorage.getItem("slug") === "team_leader") {
      navigate(quickReply || home, { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    if (localStorage.getItem("slug") === "team_leader") {
      return;
    }
    dispatch(resetUsersList());
    let timeOut = setTimeout(() => {
      dispatch(resetUsersList());
      dispatch(
        getUsers({
          search,
          page,
          startDate,
          endDate,
          status,
          branch,
          role,
        })
      );
    }, 800);
    return () => clearTimeout(timeOut);
  }, [branch, dispatch, endDate, page, role, search, startDate, status]);
  useEffect(() => {
    if (localStorage.getItem("slug") === "team_leader") {
      return;
    }
    dispatch(getUserRole());
    dispatch(getFrontEndBranch());
  }, [dispatch]);
  const { getUsersData, totalPage, getFrontEndBranchList, getUserRoleList } =
    useSelector((state) => state.commonReducer);
  const onPageChange = (number) => {
    setPage(number);
  };
  const paginationFilter = {
    search,
    status,
    startDate,
    endDate,
    branch,
  };

  const updateQueryString = (key, value) => {
    if (value === "" || value === undefined || value === null) {
      queryParameters.delete(key);
    } else {
      queryParameters.set(key, value);
    }

    if (key !== "page") {
      queryParameters.delete("page");
      setPage(1);
    }

    navigate(`${location.pathname}?${queryParameters.toString()}`, {
      replace: true,
    });
  };

  const handleExportCsv = async () => {
    try {
      setExportingCsv(true);
      const response = await postApi(`${baseUrl}/api/users/export-buffer`, "post", {
        search,
        startDate,
        endDate,
        status,
        branch,
        role,
      });

      if (!response?.status || !response?.buffer) {
        return;
      }

      const decodedBuffer = atob(response.buffer);
      const jsonText = new TextDecoder().decode(
        Uint8Array.from(decodedBuffer, (character) => character.charCodeAt(0))
      );
      const exportRows = JSON.parse(jsonText);

      const csvBlob = buildCsvBlob(exportRows);
      const fileUrl = URL.createObjectURL(csvBlob);
      const link = document.createElement("a");
      link.href = fileUrl;
      link.download = response?.fileName || `users-report-${Date.now()}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(fileUrl);
    } catch (error) {
      console.error("CSV export failed:", error);
    } finally {
      setExportingCsv(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    const result = await dispatch(deleteUserByAdmin(deleteTarget.id));
    setDeleting(false);
    setDeleteTarget(null);
    if (result?.payload?.status) {
      await dispatch(resetUsersList());
      dispatch(getUsers({ search, page, startDate, endDate, status, branch, role }));
    }
  };

  return (
    <>
      <SideMenu />
      <div className="mt-extra content container-fluid">
        {branchName && (
          <Row>
            <Col md={12} className="text-center">
              <h3 className="text-black">{branchName}</h3>
            </Col>
            <hr />
          </Row>
        )}
        <Row className="justify-content-end">
          <Col>
            <Button variant="primary" className="mx-2 text-white">
              <LastLocation />
            </Button>
            {!branch && <UserAction api={addUser} title={"Add User"} />}
            {localStorage.getItem("slug") === "admin" && (
              <Button
                variant="primary"
                className="mx-2"
                onClick={handleExportCsv}
                disabled={exportingCsv}
              >
                {exportingCsv ? "Exporting..." : "Export CSV"}
              </Button>
            )}
          </Col>

          <Col md={1}>
            <Button
              variant="danger"
              onClick={() => {
                navigate(users);
                window.location.reload();
              }}
            >
              Clear
            </Button>
          </Col>
        </Row>
        <Row className="mb-1 mt-2">
          <Col md={3}>
            <Form.Label>Search</Form.Label>
            <InputGroup>
              <InputGroup.Text id="inputGroup-sizing-default">
                <i className="bi bi-search"></i>
              </InputGroup.Text>
              <Form.Control
                aria-label="Default"
                placeholder="Search anything..."
                aria-describedby="inputGroup-sizing-default"
                onChange={(e) => setSearch(e.target.value)}
              />
            </InputGroup>
          </Col>
          <Col md={2} sx={6} xs={6}>
            <Form.Group>
              <Form.Label>Start Date</Form.Label>
              <Form.Control
                type="date"
                placeholder="Start Date"
                name="startDate"
                value={startDate}
                max={moment().format("YYYY-MM-DD")}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  updateQueryString("startDate", e.target.value);
                }}
              />
            </Form.Group>
          </Col>
          <Col md={2} sx={6} xs={6}>
            <Form.Group>
              <Form.Label>End Date</Form.Label>
              <Form.Control
                type="date"
                placeholder="End Date"
                name="endtDate"
                value={endDate}
                max={moment().format("YYYY-MM-DD")}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  updateQueryString("endDate", e.target.value);
                }}
              />
            </Form.Group>
          </Col>
          <Col
            md={1}
            sx={6}
            xs={6}
            className={width.current > 500 ? "" : "mt-1"}
          >
            <Form.Label>Status</Form.Label>
            <Form.Select
              onChange={(e) => {
                setStatus((/true/).test(e.target.value));
                updateQueryString("status", e.target.value);
              }}
              value={status}
              className="fancyy-select-dropdown"
            >
              <option value={""}>Select Status</option>
              <option value={true} className="text-success">
                <b>Active</b>
              </option>
              <option value={false} className="text-danger">
                <b>InActive</b>
              </option>
            </Form.Select>
          </Col>
          <Col
            md={2}
            sx={6}
            xs={6}
            className={width.current > 500 ? "" : "mt-1"}
          >
            <Form.Label>Role</Form.Label>
            <Form.Select
              onChange={(e) => {
                setRole(e.target.value);
                updateQueryString("role", e.target.value);
              }}
              value={role}
              className="fancyy-select-dropdown"
            >
              <option value={""}>Select Role</option>
              {localStorage.getItem("slug") === "admin" ? (
                !!getUserRoleList &&
                getUserRoleList?.map(
                  (role, index) =>
                    role?.slug !== "admin" && (
                      <option key={index} value={role?.slug}>
                        {role?.name}
                      </option>
                    )
                )
              ) : (
                <>
                  <option value={"team_leader"}>Team Leader</option>
                  <option value={"customer"}>Customer</option>
                </>
              )}
            </Form.Select>
          </Col>
          {localStorage.getItem("slug") === "admin" && (
            <Col
              md={2}
              sx={6}
              xs={6}
              className={width.current > 500 ? "" : "mt-1"}
            >
              <Form.Label>Branch</Form.Label>
              <Form.Select
                onChange={(e) => {
                  setBranch(e.target.value);
                  updateQueryString("branch", e.target.value);
                }}
                value={branch}
                className="fancyy-select-dropdown"
              >
                <option value={""}>Select Branch</option>
                {!!getFrontEndBranchList &&
                  getFrontEndBranchList?.map((branch, index) => (
                    <option key={index} value={branch?.id}>
                      {branch?.name}
                    </option>
                  ))}
              </Form.Select>
            </Col>
          )}
        </Row>
        <Row className="no-more-tables no-more-tables-font">
          <table className={width.current > 500 ? "table w-100" : null}>
            <thead>
              <tr className="tables">
                <th>Sr</th>
                {/* <th>Profile</th> */}
                <th>Name</th>
                <th>Phone</th>
                <th>Chat</th>
                {/* <th>Media</th> */}
                <th>Email</th>
                <th>Role</th>
                <th>Branch</th>
                <th>Date</th>
                <th>Block</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody className="tbody-table">
              {!!getUsersData &&
                getUsersData?.map((item, index) => {
                  return (
                    <tr key={index} className="mbl-table-card">
                      <td data-title="Sr.">{index + 1}</td>
                      {/* <td data-title="Profile">
                        <img
                          className="profile-img"
                          src={`${item?.image}`}
                          alt={item?.firstName}
                        />
                      </td> */}
                      <td data-title="Name">
                        {`${item?.firstName} ${item?.lastName}`}
                      </td>
                      <td data-title="Phone">{item?.phone}</td>
                      <td>
                        <Button
                          variant="primary"
                          onClick={() =>
                            navigate(`/admin-chat-users?specificUserId=${item?.id}`)
                          }
                        >
                          <i className="bi bi-eye-fill"></i>
                        </Button>
                      </td>
                      {/* <td>
                        <Button
                          variant="success"
                          onClick={() =>
                            navigate(`${uploadedFiles}?id=${item?.id}`)
                          }
                        >
                          <i className="bi bi-eye-fill"></i>
                        </Button>
                      </td> */}
                      <td data-title="Email">{item?.email}</td>
                      <td data-title="Role">
                        <b>
                          {(item?.role?.name === "Customer" && (
                            <span className="text-success">
                              {item?.role?.name}
                            </span>
                          )) ||
                            (item?.role?.name === "Team Leader" && (
                              <span className="text-primary">
                                {item?.role?.name}
                              </span>
                            )) ||
                            (item?.role?.name === "Branch Leader" && (
                              <span className="text-info">
                                {item?.role?.name}
                              </span>
                            ))}
                        </b>
                      </td>
                      <td data-title="Branch">{item?.branch?.name}</td>
                      <td data-title="Date">
                        {moment(item?.updatedAt).format("DD/MM/YY")}
                      </td>
                      <td data-title="Black" className="text-center">
                      <i className={`${item?.status ? "bi bi-database-fill-lock text-danger" : 'bi bi-unlock-fill text-success'}`}
                      style={{ cursor: "pointer" }}
                      onClick={async () => {
                        const confirmed = window.confirm("Are you sure you want to proceed?");
                        if (confirmed) {
                          const apiResp = await dispatch(editProfileByAdmin({
                            id : item?.id,
                            status : !item?.status
                          }))
                          if(apiResp?.payload?.status){
                            await dispatch(getUsers());
                          }
                        }
                      }}
                      ></i>
                      </td>
                      <td data-title="Status">
                        <b>
                          {item?.status ? (
                            <span className="text-success">Active</span>
                          ) : (
                            <span className="text-danger">InActive</span>
                          )}
                        </b>
                      </td>
                      <td
                        data-title="Action"
                        // className="d-flex justify-content-center"
                      >
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          {item?.role?.slug === "customer" ? <UpdateCustomer
                            title={"Update User"}
                            id={item?.id}
                            image={item?.image}
                            firstName={item?.firstName}
                            lastName={item?.lastName}
                            phone={item?.phone}
                            role={item?.role?.slug}
                            branch={item?.branch?.id}
                            status={item?.status}
                            api={editProfileByAdmin}
                          /> : <UserAction
                            title={"Update User"}
                            id={item?.id}
                            image={item?.image}
                            firstName={item?.firstName}
                            lastName={item?.lastName}
                            phone={item?.phone}
                            email={item?.email}
                            addressline1={item?.addressline1}
                            addressline2={item?.addressline2}
                            city={item?.city}
                            zipcode={item?.zipcode}
                            state={item?.state}
                            role={item?.role?.slug}
                            branch={item?.branch?.id}
                            status={item?.status}
                            api={editProfileByAdmin}
                          />}
                          {localStorage.getItem("slug") === "admin" && (
                            <Button
                              variant="danger"
                              size="sm"
                              title="Delete user and all their data"
                              onClick={() => setDeleteTarget({ id: item?.id, name: `${item?.firstName} ${item?.lastName}` })}
                            >
                              <i className="bi bi-trash3-fill"></i>
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </Row>
        <Row className="mt-3">
          <Col>
            <NumberPagination
              pageCount={totalPage}
              currentPage={page}
              api={getUsers}
              onPageChange={onPageChange}
              pageurl={users}
              filters={paginationFilter}
              shouldDispatch={false}
            />
          </Col>
        </Row>
      </div>

      {/* Delete confirmation modal */}
      <Modal show={!!deleteTarget} onHide={() => !deleting && setDeleteTarget(null)} centered>
        <Modal.Header closeButton={!deleting}>
          <Modal.Title className="text-danger">
            <i className="bi bi-exclamation-triangle-fill me-2"></i>Delete User
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to permanently delete{" "}
            <strong>{deleteTarget?.name}</strong>?
          </p>
          <p className="text-danger mb-0">
            <small>
              This will delete all their messages, chat history, group memberships,
              and notifications. <strong>This action cannot be undone.</strong>
            </small>
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteConfirm} disabled={deleting}>
            {deleting ? "Deleting..." : "Yes, Delete Permanently"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Users;
